import React from 'react';

import { css } from '@emotion/css';
import { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { TextToast } from '@/components/toast/TextToast.tsx';
import { usePatientFromURL } from '@/hooks/usePatientIdFromURL';
import { usePatientNote } from '@/hooks/usePatientNote.ts';
import { useStyles } from '@/hooks/useStyles';
import { DiabetesInfo } from '@/pages/patient-medical-file/DiabetesInfo';
import { Entourage } from '@/pages/patient-medical-file/Entourage';
import { MedicalDeviceList } from '@/pages/patient-medical-file/MedicalDeviceList';
import { PatientProfile } from '@/pages/patient-medical-file/PatientProfile';
import { useScreenSize } from '@/theme/responsive.ts';
import { PatientNote } from '@/uiKit/organisms/PatientNote.tsx';

export const PatientMedicalFile: React.FC = () => {
  const styles = useStyles(makeStyles);
  const patient = usePatientFromURL();
  const screenSize = useScreenSize();
  const { t } = useTranslation();
  const note = usePatientNote(patient.id, () =>
    TextToast.success(t('pages.patientMedicalFile.noteSuccess')),
  );
  switch (screenSize) {
    case 'SM':
    case 'MD':
    case 'LG':
      return (
        <div className={styles.container}>
          <PatientProfile patientId={patient.id} />
          <MedicalDeviceList patientId={patient.id} />
          <PatientNote note={note} className={styles.noteSmall} />
          <DiabetesInfo patientId={patient.id} />
          <Entourage patientId={patient.id} />
        </div>
      );
    default:
      return (
        <div className={styles.container}>
          <div className={styles.top}>
            <div className={styles.aside}>
              <PatientProfile patientId={patient.id} />
              <MedicalDeviceList patientId={patient.id} />
              <DiabetesInfo patientId={patient.id} />
            </div>
            <PatientNote
              note={note}
              className={styles.noteLarge}
              classes={{ content: styles.noteContentLarge }}
            />
          </div>
          <Entourage patientId={patient.id} />
        </div>
      );
  }
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${theme.spacing(12)};
  `,
  content: css`
    display: flex;
    flex-direction: row;
    margin-top: ${theme.spacing(12)};
    flex-wrap: wrap-reverse;
    gap: ${theme.spacing(8)};
  `,
  top: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(8)};
  `,
  aside: css`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    gap: ${theme.spacing(8)};
  `,
  noteLarge: css`
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
  `,
  noteContentLarge: css`
    flex: 1 1 0;
  `,
  noteSmall: css`
    flex: 1 1 auto;
    flex-direction: column;
    display: flex;
    max-height: 90vh;
  `,
});
